/**
 * SalidaCard — variante de VoyageCard para la vista de Salidas Programadas.
 *
 * Muestra la salida como una tarjeta con time-block (hora de zarpe), nombre
 * del paquete, punto de embarque, lanchas asignadas, medidor de cupos, chips
 * de estado + clima y acciones CRUD. NO incluye la lista de reservas por
 * dentro — para ver reservas el operador va al módulo Booking (evita
 * duplicar semántica entre módulos).
 *
 * Mantiene el mismo lenguaje visual que VoyageCard para que HoyPage /
 * ProximasPage y SalidasPage se sientan como partes del mismo sistema.
 */
import React from 'react';
import {
  Box, Typography, Chip, IconButton, LinearProgress, Tooltip, Stack, Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

// Colores por estado — alineados con ESTADO_COLORS de SalidasPage
const ESTADO_STYLES = {
  programada: { bg: '#e0f2fe', color: '#075985', accent: '#0284c7', label: 'Programada' },
  activa:     { bg: '#dcfce7', color: '#166534', accent: '#16a34a', label: 'Activa' },
  completada: { bg: '#f1f5f9', color: '#475569', accent: '#64748b', label: 'Completada' },
  cancelada:  { bg: '#fee2e2', color: '#991b1b', accent: '#dc2626', label: 'Cancelada' },
  suspendida: { bg: '#fef3c7', color: '#92400e', accent: '#f59e0b', label: 'Suspendida' },
};

const CLIMA_STYLES = {
  normal:    { emoji: '☀️', label: 'Normal',    color: '#16a34a', bg: '#dcfce7' },
  lluvia:    { emoji: '🌧️', label: 'Lluvia',    color: '#0ea5e9', bg: '#e0f2fe' },
  tormenta:  { emoji: '⛈️', label: 'Tormenta',  color: '#dc2626', bg: '#fee2e2' },
  cancelado: { emoji: '🚫', label: 'Cancelado', color: '#991b1b', bg: '#fee2e2' },
};

function TimeBlock({ hora }) {
  const [hh, mm] = String(hora || '').split(':');
  const label = hh && mm ? `${hh}:${mm}` : (hora || '—');
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      px: 0.9, py: 0.6, bgcolor: '#0f172a', color: '#f8fafc',
      borderRadius: 1, minWidth: 46, lineHeight: 1, flexShrink: 0,
    }}>
      <Typography sx={{
        fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 8, fontWeight: 700, mt: 0.2,
        textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.55,
      }}>
        Zarpe
      </Typography>
    </Box>
  );
}

/**
 * SalidaCard — props:
 *   salida:   objeto salida del backend
 *   onEdit:   () => void
 *   onSuspender: () => void  (opcional; oculto si la salida ya está suspendida/cancelada)
 *   onDelete: () => void  (opcional)
 */
export default function SalidaCard({ salida, onEdit, onSuspender, onDelete }) {
  const s = salida || {};
  const estado = ESTADO_STYLES[s.estado] || ESTADO_STYLES.programada;
  const clima = CLIMA_STYLES[s.estado_clima || 'normal'] || CLIMA_STYLES.normal;

  const cap = Number(s.capacidad_total || 0);
  const res = Number(s.asientos_reservados || 0);
  const pct = cap > 0 ? Math.min((res / cap) * 100, 100) : 0;
  const pctColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a';

  // El backend expone la relación M:N como `embarcaciones_asignadas`. El
  // fallback cubre salidas viejas con el campo legacy `embarcacion_nombre`.
  const lanchas = (s.embarcaciones_asignadas || [])
    .map(ea => ea.embarcacion_nombre).filter(Boolean);
  if (lanchas.length === 0 && s.embarcacion_nombre) lanchas.push(s.embarcacion_nombre);

  const suspendible = onSuspender && !['suspendida', 'cancelada'].includes(s.estado);

  return (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: `3px solid ${estado.accent}`,
      borderRadius: 2,
      overflow: 'hidden',
      transition: 'box-shadow 0.15s, transform 0.15s',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
      },
    }}>
      {/* HEADER — dos filas, todo el contenido cabe aunque la card sea angosta.
          Fila 1: [TimeBlock] [Nombre paquete (ellipsis)] [Chip estado] [Emoji clima]
          Fila 2: ⚓ Punto embarque · Regreso HH:MM  (opcional, si hay dato) */}
      <Box sx={{
        p: 1,
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #f1f5f9',
      }}>
        <Box sx={{
          display: 'grid',
          // minmax(0, 1fr) permite que el título se contraiga con ellipsis
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          gap: 0.75,
          alignItems: 'center',
        }}>
          <TimeBlock hora={s.hora_salida} />

          <Typography sx={{
            fontSize: { xs: 13, sm: 14 }, fontWeight: 800, color: '#0f172a',
            lineHeight: 1.25, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={s.paquete_nombre || ''}>
            {s.paquete_nombre || '—'}
          </Typography>

          {/* Chips de estado + clima juntos, compactos. En tarjetas MUY angostas
              (grid lg 3-col) el label de estado se recorta al iniciar (E/A/C/S)
              con tooltip completo. */}
          <Stack direction="row" spacing={0.4} alignItems="center" flexShrink={0}>
            <Tooltip title={`Estado: ${estado.label}`}>
              <Chip size="small" label={estado.label} sx={{
                bgcolor: estado.bg, color: estado.color,
                fontSize: 9.5, height: 20, fontWeight: 700,
                '& .MuiChip-label': { px: 0.75 },
              }} />
            </Tooltip>
            <Tooltip title={`Clima: ${clima.label}`}>
              <Chip size="small" label={clima.emoji} sx={{
                bgcolor: clima.bg, color: clima.color,
                fontSize: 10.5, height: 20, fontWeight: 700,
                minWidth: 26, width: 26,
                '& .MuiChip-label': { px: 0.25 },
              }} />
            </Tooltip>
          </Stack>
        </Box>

        {/* Fila 2: metadata (punto de embarque + regreso). Va debajo, no
            compite por espacio horizontal con el título. */}
        {(s.punto_embarque_nombre || s.hora_regreso) && (
          <Box sx={{
            mt: 0.5, pl: 0.25,
            display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap',
          }}>
            {s.punto_embarque_nombre && (
              <Typography sx={{
                fontSize: 10.5, color: '#64748b',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                minWidth: 0,
              }} title={s.punto_embarque_nombre}>
                ⚓ {s.punto_embarque_nombre}
              </Typography>
            )}
            {s.punto_embarque_nombre && s.hora_regreso && (
              <Box component="span" sx={{ color: '#cbd5e1', fontSize: 10.5 }}>·</Box>
            )}
            {s.hora_regreso && (
              <Typography sx={{ fontSize: 10.5, color: '#64748b', flexShrink: 0 }}>
                Regreso {String(s.hora_regreso).slice(0, 5)}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* BODY: cupos + lanchas + acciones. Padding reducido para dar aire
          al contenido en tarjetas angostas. */}
      <Box sx={{ p: 1 }}>
        {/* Ocupación */}
        <Box sx={{ mb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.35, gap: 0.5 }}>
            <Typography sx={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
              fontWeight: 700, color: '#64748b',
            }}>
              Cupos
            </Typography>
            <Typography sx={{
              fontSize: 11.5, fontWeight: 700, color: pctColor,
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {res}/{cap} · {pct.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct}
            sx={{
              height: 5, borderRadius: 3, bgcolor: '#f1f5f9',
              '& .MuiLinearProgress-bar': { bgcolor: pctColor, borderRadius: 3 },
            }} />
        </Box>

        {/* Lanchas asignadas — cada chip se puede recortar con maxWidth para
            no romper el layout con nombres largos. */}
        {lanchas.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mb: 0.75 }}>
            {lanchas.map((n, i) => (
              <Tooltip key={i} title={n}>
                <Chip size="small"
                  icon={<DirectionsBoatIcon sx={{ fontSize: '12px !important', color: '#075985 !important' }} />}
                  label={n}
                  sx={{
                    bgcolor: '#e0f2fe', color: '#075985',
                    fontSize: 10, height: 20, fontWeight: 600,
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      pl: 0.25,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    },
                  }} />
              </Tooltip>
            ))}
          </Box>
        )}
        {lanchas.length === 0 && (
          <Chip size="small" label="Sin botes asignados"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 10, height: 20, fontWeight: 700, mb: 0.75 }} />
        )}

        {/* Notas (si hay) — con word-break para no rebosar la card */}
        {s.notas && (
          <Typography sx={{
            fontSize: 10.5, color: '#475569', fontStyle: 'italic',
            mb: 0.75, p: 0.6, bgcolor: '#f8fafc', borderRadius: 1,
            borderLeft: '2px solid #cbd5e1', lineHeight: 1.35,
            overflowWrap: 'break-word', wordBreak: 'break-word',
            // Máximo 3 líneas para no explotar la card
            display: '-webkit-box',
            WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }} title={s.notas}>
            {s.notas}
          </Typography>
        )}

        {/* Promoción vigente */}
        {s.promo_aplicada && (
          <Tooltip title={s.promo_aplicada.nombre}>
            <Chip size="small" label={`🏷️ ${s.promo_aplicada.nombre}`}
              sx={{
                bgcolor: '#fef3c7', color: '#92400e',
                fontSize: 10, height: 20, fontWeight: 700, mb: 0.75,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                },
              }} />
          </Tooltip>
        )}

        {/* Acciones — alineadas a la derecha, con wrap para no rebosar */}
        <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {onEdit && (
            <Button size="small" onClick={onEdit}
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              sx={{ textTransform: 'none', fontSize: 10.5, fontWeight: 600,
                     color: '#0f766e', minWidth: 'auto', px: 0.75, py: 0.25 }}>
              Editar
            </Button>
          )}
          {suspendible && (
            <Button size="small" onClick={onSuspender}
              startIcon={<EventBusyIcon sx={{ fontSize: 13 }} />}
              sx={{ textTransform: 'none', fontSize: 10.5, fontWeight: 600,
                     color: '#dc2626', minWidth: 'auto', px: 0.75, py: 0.25 }}>
              Suspender
            </Button>
          )}
          {onDelete && (
            <IconButton size="small" onClick={onDelete} sx={{ color: '#94a3b8', p: 0.5 }}
              aria-label="Eliminar salida">
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
